import React, { createContext, useState, useContext, useEffect } from 'react';

// Create context
const OnboardingContext = createContext();

// Custom hook to use the onboarding context
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

// Provider component
export const OnboardingProvider = ({ children }) => {
  const [onboardingState, setOnboardingState] = useState({
    hasCompletedOnboarding: false,
    currentStep: 'welcome',
    completedSteps: {},
    showTooltips: true
  });
  
  // Load onboarding state from localStorage on initial render
  useEffect(() => {
    const savedState = localStorage.getItem('resumemo_onboarding_state');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        setOnboardingState(prevState => ({
          ...prevState,
          ...parsedState
        }));
      } catch (error) {
        console.error('Error parsing onboarding state:', error);
      }
    }
  }, []);
  
  // Save onboarding state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('resumemo_onboarding_state', JSON.stringify({
      hasCompletedOnboarding: onboardingState.hasCompletedOnboarding,
      currentStep: onboardingState.currentStep,
      completedSteps: onboardingState.completedSteps
    }));
  }, [onboardingState.hasCompletedOnboarding, onboardingState.currentStep, onboardingState.completedSteps]);
  
  // Mark a step as completed
  const completeStep = (stepId) => {
    setOnboardingState(prevState => ({
      ...prevState,
      completedSteps: {
        ...prevState.completedSteps,
        [stepId]: true
      }
    }));
  };
  
  // Set the current onboarding step
  const setCurrentStep = (stepId) => {
    setOnboardingState(prevState => ({
      ...prevState,
      currentStep: stepId
    }));
  };
  
  // Complete the entire onboarding process
  const completeOnboarding = () => {
    setOnboardingState(prevState => ({
      ...prevState,
      hasCompletedOnboarding: true
    }));
  };
  
  // Reset the onboarding process
  const resetOnboarding = () => {
    setOnboardingState({
      hasCompletedOnboarding: false,
      currentStep: 'welcome',
      completedSteps: {},
      showTooltips: true
    });
    localStorage.removeItem('resumemo_onboarding_state');
  };
  
  // Toggle tooltips visibility
  const toggleTooltips = (show) => {
    setOnboardingState(prevState => ({
      ...prevState,
      showTooltips: show !== undefined ? show : !prevState.showTooltips
    }));
  };
  
  // Check if a specific step is completed
  const isStepCompleted = (stepId) => {
    return !!onboardingState.completedSteps[stepId];
  };
  
  const value = {
    ...onboardingState,
    completeStep,
    setCurrentStep,
    completeOnboarding,
    resetOnboarding,
    toggleTooltips,
    isStepCompleted
  };
  
  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;